import { getDateFromFilters } from '@/components/SupplyManagement/SupplyDirectory/util'
import { getCurrentMarket } from '@/utils/general'
import type { SupplyDirectoryHeaderFilters } from '@components/SupplyManagement/types'
import { NotificationMessage } from '@gravitate-js/excalibrr'
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@utils/api'
import { metadataQuery } from './util'

const KEYS = {
  priceOverview: 'priceOverviewQuery' as const,
  supplyOutages: 'supplyOutageQuery' as const,
}

const onOutageChange = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.cancelQueries({ queryKey: [KEYS.supplyOutages] }),
    queryClient.cancelQueries({ queryKey: [KEYS.priceOverview] }),
  ])

const onOutageChangeSettled = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: [KEYS.supplyOutages] }),
    queryClient.invalidateQueries({ queryKey: [KEYS.priceOverview] }),
  ])

export const usePrices = () => {
  const queryClient = useQueryClient()
  const market = getCurrentMarket()

  const usePriceOverviewQuery = ({
    dateControl,
    filter,
    priceLimit,
    groupBy,
    siteInfo,
  }: SupplyDirectoryHeaderFilters) =>
    useQuery({
      queryKey: [KEYS.priceOverview, dateControl, filter, priceLimit, groupBy, siteInfo],
      queryFn: () => {
        const payload = {
          filter: { ...filter, as_of: getDateFromFilters(dateControl, filter.as_of) },
          price_limit: priceLimit,
          group_by: groupBy,
        }
        let site_info = undefined
        if (siteInfo) {
          try {
            const siteOption = JSON.parse(siteInfo.siteOptionJSON)
            site_info = {
              site_number: siteOption.number,
              only_in_network_terminals: siteInfo.onlyInNetworkTerminals ?? true,
            }
          } catch (error) {
            console.error('Error parsing site option:', siteInfo.siteOptionJSON, error)
          }
        }
        return api.priceManagementOverview({ ...payload, site_info })
      },
      refetchInterval: 60_000,
    })

  const useSupplyOutageQuery = ({ filter: { as_of }, dateControl }: SupplyDirectoryHeaderFilters) =>
    useQuery({
      queryKey: [KEYS.supplyOutages, dateControl, as_of],
      queryFn: () => api.getSupplyOutages(getDateFromFilters(dateControl, as_of)),
      refetchInterval: 60_000,
    })

  const supplyOutageMetadataQuery = useQuery({
    queryKey: ['supplyOutageMetadataQuery'],
    queryFn: () => api.getMetadata(metadataQuery(market)),
  })

  const uploadPricesMutation = useMutation({
    mutationKey: ['uploadPricesMutation'],
    mutationFn: (file) => api.uploadPriceExcel(file),
    onSuccess: async (_response, _request) => {
      await queryClient.invalidateQueries({
        queryKey: ['priceOverviewQuery'],
      })
    },
  })

  const upsertSupplyOutageMutation = () =>
    useMutation({
      mutationKey: ['upsertSupplyOutageMutation'],
      mutationFn: ({ outage, as_of_utc }: { outage: Gravitate.SupplyOutage; as_of_utc: string }) =>
        api.upsertSupplyOutage(outage, as_of_utc),
      onMutate: () => onOutageChange(queryClient),
      onSuccess: () => NotificationMessage('Success', 'Outage saved.', false),
      onSettled: () => onOutageChangeSettled(queryClient),
    })

  const cancelSupplyOutageMutation = () =>
    useMutation({
      mutationKey: ['cancelSupplyOutageMutation'],
      mutationFn: (outage_id: string) => api.cancelSupplyOutage(outage_id),
      onMutate: () => onOutageChange(queryClient),
      // When mutate is called:
      onSuccess: () => NotificationMessage('Success', 'Outage cancelled.', false),
      onSettled: () => onOutageChangeSettled(queryClient),
    })

  const updateSupplyRowMutation = () =>
    useMutation({
      mutationKey: ['updateSupplyRowMutation'],
      mutationFn: async ({ updatedRow, as_of }: { updatedRow: Gravitate.SupplyManagementOption; as_of: string }) => {
        return api.updateConstraints({
          min: updatedRow.directive_min,
          max: updatedRow.directive_max,
          curve_group_id: updatedRow.group_id,
          as_of: as_of,
        })
      },
      onError: (error) => {
        console.error('Error saving dirty changes:', error)
        NotificationMessage('Oops!', `Unable to save directive changes.`)
      },
    })

  return {
    usePriceOverviewQuery,
    useSupplyOutageQuery,
    uploadPricesMutation,
    upsertSupplyOutageMutation,
    cancelSupplyOutageMutation,
    supplyOutageMetadata: supplyOutageMetadataQuery.data?.data,
    updateSupplyRowMutation,
  }
}
